<template>
  <div class="m-3 mb-4">
    <b-row>
      <b-col>
        <b-row>
          <b-col class="pl-0">
            <label v-if="isAvailable && canAdd">
              <span :class="hasReachedLimit && adminCanBypassLimit ? 'text-danger' : ''">
                {{ trans('add_to_cart') }}
              </span>
              <small
                v-if="hasReachedLimit && adminCanBypassLimit"
                class="text-danger"
                :title="trans('package_admin_reached_limit')"
                v-b-tooltip.hover
              >
                <i class="fa fa-fw fa-info-circle"></i>
              </small>
              <small v-if="requiresCoupon" class="text-muted">
                ({{ trans('requires_coupon') }})
              </small>
            </label>
            <em v-if="!isAvailable && canAdd && !alreadyAdded" class="text-muted">
              {{ trans('no_slots_available') }}
            </em>
            <em v-if="!canAdd" class="text-muted">
              {{ trans('package_reached_limit') }}
            </em>
          </b-col>
        </b-row>
        <b-row v-if="isAvailable && canAdd">
          <b-col
            v-for="(locations, index) in locationDropdowns"
            :key="index"
            class="pl-0 pr-1 col-auto mb-2"
            v-if="rule_locations.length == locationDropdowns.length && !!locations"
          >
            <b-dropdown
              v-if="!!locations"
              :variant="hasReachedLimit && adminCanBypassLimit ? 'outline-danger' : 'outline-branded'"
              size="sm"
              :text="rule_locations[index].name"
              :options="locations"
            >
              <b-dropdown-item v-for="location in locations" :key="location.id" @click="onClickLocation(index, location)">
                <i :class="`fa fa-fw ${location.id == rule_locations[index].id ? 'fa-check' : ''}`"></i>
                {{ location.name }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
          <b-col v-if="requiresCoupon && !showNbCoupons" class="px-1 col-auto mb-2">
            <b-form-group class="mb-0">
              <b-form-input
                v-model="code"
                size="sm"
                :placeholder="trans('coupon')"
                :state="validation"
                :disabled="busy"
                @keydown="onCouponKeydown"
              />
              <b-form-invalid-feedback :state="validation">
                {{ error }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col v-if="requiresMembership && !!invoice && !!invoice.recipient" class="px-1 col-auto mb-2">
            <choose-client-dropdown
              :organization="organization"
              :invoice="invoice"
              type="member"
              v-model="member_id"
              :disabled="busy"
              @connected="onConnected"
              @disconnected="onDisconnected"
            ></choose-client-dropdown>
          </b-col>
          <b-col class="px-1 col-auto mb-2">
            <b-button :variant="hasReachedLimit && adminCanBypassLimit ? 'danger' : 'branded'" size="sm" class="mb-2" :disabled="busy || (requiresCoupon && !code && !showNbCoupons) || (requiresMembership && !!invoice && !!invoice.recipient && !member_id)" @click="addToCart">
              <i v-if="!busy" class="fa fa-fw fa-plus"></i>
              <span v-if="!busy">{{ trans('book' + (alreadyAdded ? '_again' : '')) }}</span>
              <i v-if="busy" class="fa fa-fw fa-circle-notch fa-spin"></i>
            </b-button>
            <p v-if="showNbCoupons">
              {{ trans('you_have_coupons_left', {nb: nbCoupons}) }}
            </p>
          </b-col>
        </b-row>
        <b-row v-if="canShowConnectForCoupons">
          <b-col>
            <p>
              {{ trans('client_connect_to_apply_coupon') }}
              <b-button
                variant="outline-branded"
                size="sm"
                class="py-0 ml-2"
                @click="show_connect_for_coupons = !show_connect_for_coupons"
                :disabled="busy"
              >
                {{ trans(show_connect_for_coupons ? 'hide' : 'connect') }}
              </b-button>
            </p>
            <connect
              v-if="show_connect_for_coupons"
              :organization="organization"
              :invoice="invoice"
              :reload-on-success="false"
              @connected="onConnected"
            ></connect>
          </b-col>
        </b-row>
        <b-row v-if="show_connect">
          <b-col>
            <connect
              :organization="organization"
              :invoice="invoice"
              type="member"
              :reload-on-success="false"
              :title="organization.amilia_events_settings['membership_required_message_'+lang] || trans('member_not_connected')"
              @connected="onConnected"
            ></connect>
          </b-col>
        </b-row>
        <b-row v-if="show_account_owner_form">
          <b-col>
            <account-owner-form
              :organization="organization"
              :invoice="invoice"
              :form="accountOwnerForm"
              :next-button-label="trans('save')"
              :previous-button-label="trans('cancel')"
              @invoice="onSaveAccountOwnerForm"
              @previous="show_account_owner_form = false"
              @client="$emit('client', $event)"
              @last-invoice-recipient="$emit('last-invoice-recipient', $event)"
            ></account-owner-form>
          </b-col>
        </b-row>
      </b-col>
      <b-col v-if="pkg.show_site_plan" class="col-auto pl-0 pl-md-2">
        <b-button
          v-if="pkg.show_site_plan"
          variant="link"
          class="d-block text-branded pt-0 px-0"
          v-b-modal.show-site-plan-modal
        >
          <i class="fa fa-fw fa-map"></i>
          {{ trans('site_plan') }}
        </b-button>
        <b-link href="#" v-b-modal.show-site-plan-modal>
          <b-img fluid :src="pkg.image_url3" style="max-width: 20vw;" />
        </b-link>
      </b-col>
    </b-row>
    <show-site-plan-modal
      v-if="pkg.show_site_plan"
      :image-url="pkg.image_url3"
      :title="trans('site_plan')"
    ></show-site-plan-modal>
  </div>
</template>

<script>
  import ShowSitePlanModal from './ShowSitePlanModal'
  import Connect from './../client/Connect'
  import ChooseClientDropdown from './../client/ChooseClientDropdown'
  import AccountOwnerForm from './AccountOwnerForm'

  export default {
    props: {
      organization: Object,
      invoice: Object,
      pkg: Object,
      alreadyAdded: Boolean,
      addedThisTimeCount: Number,
      addedAnyTimeCount: Number,
      availability: Object,
    },
    components: {
      ShowSitePlanModal,
      Connect,
      ChooseClientDropdown,
      AccountOwnerForm,
    },
    data() {
      return {
        trans: trans,
        lang: lang,
        error: null,
        busy: false,
        rule_locations: [],
        code: null,
        show_connect: false,
        show_connect_for_coupons: false,
        member_id: null,
        try_force: false,
        package_form: null,
        show_account_owner_form: false,
      }
    },
    computed: {
      adminCanBypassLimit() {
        return !!this.organization.auth_user &&
          this.organization.auth_user.microsite_permissions.can_bybass_limit
      },
      hasReachedLimit() {
        return this.pkg.limit_per_cart >= 1 &&
          this.addedAnyTimeCount >= this.pkg.limit_per_cart ||
          this.pkg.limit_per_slot >= 1 &&
          this.addedThisTimeCount >= this.pkg.limit_per_slot
      },
      canAdd() {
        return !this.hasReachedLimit || this.adminCanBypassLimit
      },
      isAvailable() {
        return this.availability &&
          this.availability.extendedProps.availability &&
          this.availability.extendedProps.availability.length > 0
      },
      locationDropdowns() {
        if (!this.isAvailable || (this.hasReachedLimit && !this.adminCanBypassLimit)) return []
        return _.reduce(this.availability.extendedProps.availability, (dropdowns, avail) => {
          dropdowns.push(avail.allow_choice ? avail.locations : null)
          return dropdowns
        }, [])
      },
      requiresMembership() {
        return this.pkg.requires_membership
      },
      requiresCoupon() {
        return this.pkg.requires_coupon
      },
      nbCoupons() {
        return !!this.invoice &&
          !!this.invoice.available_coupons_per_package &&
          !!this.invoice.available_coupons_per_package[this.pkg.id] ?
          this.invoice.available_coupons_per_package[this.pkg.id] :
          0
      },
      showNbCoupons() {
        if (this.requiresMembership) return false
        return this.nbCoupons > 0 && this.requiresCoupon
      },
      canShowConnectForCoupons() {
        return !!this.invoice &&
          !!this.pkg &&
          this.pkg.requires_coupon &&
          !this.requiresMembership &&
          !this.organization.connected_client &&
          !this.showNbCoupons
      },
      requiresAccountOwner() {
        return this.pkg.force_choose_players
      },
      accountOwnerForm() {
        let form = _.clone(this.pkg.form)
        form.form_questions = [_.find(form.form_questions, {type :'account_owner'})]
        return form
      },
      validation() {
        return this.error ? false : null
      },
    },
    watch: {
      locationDropdowns() {
        this.buildRuleLocations()
      },
      invoice() {
        if (this.show_connect && !!this.invoice.recipient) {
          this.show_connect = false
        }
        if (this.show_connect_for_coupons && !!this.invoice.recipient) {
          this.show_connect_for_coupons = false
        }
        this.busy = false
      },
    },
    methods: {
      onCouponKeydown(e) {
        if (e.keyCode == 13) this.addToCart()
      },
      onClickApplyExistingCoupon() {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/package/${this.package_invoice_item.id}/apply_existing_coupon`
        axios.post(url)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      buildRuleLocations() {
        this.rule_locations = []
        if (!this.isAvailable) return
        this.rule_locations = _.reduce(this.availability.extendedProps.availability, (rule_locations, avail) => {
          rule_locations.push(avail.allow_choice && avail.locations.length > 0 ? avail.locations[0] : null)
          return rule_locations
        }, [])
      },
      onClickLocation(index, location) {
        this.$set(this.rule_locations, index, location)
      },
      onSaveAccountOwnerForm(invoice) {
        this.show_account_owner_form = false
        this.busy = true
        this.$emit('invoice', invoice)
        this.$nextTick(() => {
          this.addToCart()
        })
        this.$emit('client-changed')
      },
      onConnected(invoice) {
        this.busy = true
        this.$emit('invoice', invoice)
        this.$nextTick(() => {
          if (invoice.recipient) this.member_id = invoice.recipient.id
        })
        this.$emit('client-changed')
      },
      onDisconnected(invoice) {
        this.busy = true
        this.$emit('invoice', invoice)
        this.member_id = null
        this.$emit('client-changed')
      },
      addToCart() {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/package`
        if (this.try_force) url += '?try_force=true'
        axios.post(url, {
          package_id: this.availability.extendedProps.package_id,
          start: this.availability.start,
          rule_location_ids: _.map(this.rule_locations, location => {
            return location ? location.id : null
          }),
          code: this.code,
        })
        .then(response => {
          this.code = null
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          if (error.response.status == 401 && this.requiresMembership) {
            this.show_connect = true
            return
          }
          else if (error.response.status == 401 && this.requiresAccountOwner) {
            this.show_account_owner_form = true
            return
          }
          else if (error.response.status == 422) {
            this.try_force = true
          }
          _.toastError(this, this.error)
        })
      },
    },
    mounted() {
      window.packageReservationAdd = this
      this.buildRuleLocations()
    },
  }
</script>